import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { getMarketingT } from '../utils/marketingTranslations';
import MarketingLayout from '../components/marketing/MarketingLayout';

function ServicesPage() {
  const { language } = useApp();
  const mt = getMarketingT(language);
  const isRTL = language === 'ar';

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const SERVICES = [
    { icon: '📱', title: mt('svc_qr_title'), desc: mt('svc_qr_desc'), highlights: [mt('svc_qr_h1'), mt('svc_qr_h2'), mt('svc_qr_h3')] },
    { icon: '📲', title: mt('svc_wa_title'), desc: mt('svc_wa_desc'), highlights: [mt('svc_wa_h1'), mt('svc_wa_h2'), mt('svc_wa_h3')] },
    { icon: '🌍', title: mt('svc_lang_title'), desc: mt('svc_lang_desc'), highlights: [mt('svc_lang_h1'), mt('svc_lang_h2'), mt('svc_lang_h3')] },
    { icon: '🎨', title: mt('svc_brand_title'), desc: mt('svc_brand_desc'), highlights: [mt('svc_brand_h1'), mt('svc_brand_h2'), mt('svc_brand_h3')] },
    { icon: '🖨️', title: mt('svc_print_title'), desc: mt('svc_print_desc'), highlights: [mt('svc_print_h1'), mt('svc_print_h2'), mt('svc_print_h3')] },
    { icon: '🔄', title: mt('svc_update_title'), desc: mt('svc_update_desc'), highlights: [mt('svc_update_h1'), mt('svc_update_h2'), mt('svc_update_h3')] },
  ];

  return (
    <MarketingLayout>
      <style>{`
        @keyframes mk-fadeUp {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .mk-svc-hero {
          padding: 140px 24px 80px; text-align: center;
          background: var(--bg-secondary);
          border-bottom: 1px solid var(--border-light);
        }
        .mk-svc-card {
          padding: 40px; border-radius: var(--radius-lg);
          background: var(--bg-secondary); border: 1px solid var(--border-light);
          transition: all 0.3s; display: flex; flex-direction: column;
        }
        .mk-svc-card:hover {
          transform: translateY(-6px);
          box-shadow: var(--shadow-lg); border-color: transparent;
        }
        .mk-svc-chip {
          display: inline-flex; padding: 6px 14px;
          background: var(--accent-light); color: var(--accent);
          border-radius: 100px; font-size: 12px; font-weight: 600;
        }
        @media (max-width: 768px) {
          .mk-svc-grid { grid-template-columns: 1fr !important; }
          .mk-svc-cta { padding: 48px 24px !important; }
        }
        @media (min-width: 769px) and (max-width: 1024px) {
          .mk-svc-grid { grid-template-columns: repeat(2, 1fr) !important; }
        }
      `}</style>

      {/* Hero */}
      <section className="mk-svc-hero">
        <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 16, animation: 'mk-fadeUp 0.6s ease both' }}>
          {mt('services_label')}
        </p>
        <h1 style={{
          fontFamily: isRTL ? 'var(--font-arabic)' : 'var(--font-display)',
          fontSize: 'clamp(32px, 5vw, 52px)', fontWeight: 700,
          color: 'var(--text-primary)', letterSpacing: isRTL ? '0' : '-0.03em',
          marginBottom: 20, animation: 'mk-fadeUp 0.6s ease 0.1s both',
        }}>
          {mt('services_title')}
        </h1>
        <p style={{
          fontSize: 18, color: 'var(--text-muted)', lineHeight: 1.7,
          maxWidth: 540, margin: '0 auto',
          animation: 'mk-fadeUp 0.6s ease 0.2s both',
        }}>
          {mt('services_sub')}
        </p>
      </section>

      {/* Grid */}
      <section style={{ padding: '80px 24px' }}>
        <div className="mk-svc-grid" style={{
          maxWidth: 1200, margin: '0 auto',
          display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 24,
        }}>
          {SERVICES.map((s, i) => (
            <div key={i} className="mk-svc-card">
              <div style={{ fontSize: 40, marginBottom: 20 }}>{s.icon}</div>
              <h3 style={{ fontSize: 22, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 12, fontFamily: isRTL ? 'var(--font-arabic)' : 'var(--font-body)' }}>{s.title}</h3>
              <p style={{ fontSize: 15, color: 'var(--text-muted)', lineHeight: 1.7, marginBottom: 24, flex: 1 }}>{s.desc}</p>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {s.highlights.map((h, j) => (
                  <span key={j} className="mk-svc-chip">{h}</span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section style={{ padding: '0 24px 100px' }}>
        <div className="mk-svc-cta" style={{
          maxWidth: 800, margin: '0 auto', textAlign: 'center',
          padding: '64px 48px', borderRadius: 'var(--radius-xl)',
          background: 'var(--bg-secondary)', border: '1px solid var(--border)',
          position: 'relative', overflow: 'hidden',
        }}>
          <h2 style={{
            fontFamily: isRTL ? 'var(--font-arabic)' : 'var(--font-display)',
            fontSize: 'clamp(24px, 4vw, 36px)', fontWeight: 700,
            color: 'var(--text-primary)', marginBottom: 16,
          }}>
            {mt('services_custom_title')}
          </h2>
          <p style={{ fontSize: 16, color: 'var(--text-muted)', marginBottom: 32 }}>
            {mt('services_custom_desc')}
          </p>
          <Link to="/contact" style={{
            display: 'inline-flex', padding: '14px 36px',
            background: 'var(--accent)', color: 'var(--text-inverse)',
            borderRadius: 'var(--radius-md)', textDecoration: 'none',
            fontSize: 16, fontWeight: 600,
          }}>
            {mt('services_custom_cta')}
          </Link>
        </div>
      </section>
    </MarketingLayout>
  );
}

export default ServicesPage;